import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Form, Modal, Row, Space, Spin, Switch, Tooltip, Typography, Upload, message } from 'antd';
import { Users, Home, GraduationCap, PhoneCall, Briefcase, Landmark, Trash2 } from 'lucide-react';
import { DeleteOutlined, EditOutlined, EyeOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '@stores/auth.store';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useTeachers } from '@hooks/tenant/useTeachers';
import { fetchTeachersPage } from '@services/registry';
import { exportToExcel, exportToPdf } from '@/utils/reportExporter';
import { useModuleLimit } from '@hooks/tenant/useModuleLimit';
import { useTenantSubjects } from '@hooks/tenant/useTenantSubjects';
import { useTenantClasses } from '@hooks/tenant/useTenantClasses';
import { useCreateTeacher } from '@hooks/tenant/useCreateTeacher';
import { useUpdateTeacher } from '@hooks/tenant/useUpdateTeacher';
import { useSoftDeleteTeacher } from '@hooks/tenant/useSoftDeleteTeacher';
import { useDirectoryStatusChangeFlow } from '@hooks/tenant/useDirectoryStatusChangeFlow';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { formatSubjectAssignmentList, getSubjectsAssignedToTeacher } from '@utils/teacherSubjectAssignments';
import { DashboardContainer, KPICardsGrid, StudentsHeaderActions, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from './style';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppPasswordInput, AppTextArea } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { Button } from '@components/ui/Button';
import AppAvatar from '@/components/ui/AppAvatar';
import { AppContextMenu } from '@/components/ui/AppContextMenu';
import styled, { createGlobalStyle } from 'styled-components';

const { Text } = Typography;

const TeacherRegisterModalStyles = createGlobalStyle`
  .teacher-register-modal .ant-modal-content {
    max-height: 96vh;
    display: flex;
    flex-direction: column;
  }

  .teacher-register-modal .ant-modal-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: inherit;
  }

  .teacher-register-modal .ant-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: inherit;
    margin-top: auto;
    padding-top: 14px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .teacher-register-modal .ant-modal-body {
    flex: 1;
    overflow: auto;
    max-height: calc(96vh - 160px);
    padding: 16px;
    padding-right: 10px;
    background: inherit;

    scrollbar-width: thin;
    scrollbar-color: rgba(138, 152, 167, 0.55) transparent;
  }

  .teacher-register-modal .ant-modal-body::-webkit-scrollbar {
    width: 8px;
  }

  .teacher-register-modal .ant-modal-body::-webkit-scrollbar-track {
    background: transparent;
  }

  .teacher-register-modal .ant-modal-body::-webkit-scrollbar-thumb {
    background: rgba(138, 152, 167, 0.45);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .teacher-register-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 152, 167, 0.65);
    border: 2px solid transparent;
    background-clip: content-box;
  }
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;
    &.ant-picker {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#ffffff')};
        border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
        border-radius: 12px;
        height: 44px;
        padding: 0 12px;
        font-family: var(--font-family-lufga);
        display: flex;
        align-items: center;
        box-shadow: none;
    }
    &.ant-picker:hover {
        border-color: #8a98a7;
    }
    &.ant-picker-focused {
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 2px rgba(240, 74, 74, 0.15);
    }
    .ant-picker-input > input {
        font-family: var(--font-family-lufga);
        height: 42px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : 'rgba(0, 0, 0, 0.88)')};
    }
    .ant-picker-input > input::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.35)')};
    }
    .ant-picker-suffix,
    .ant-picker-clear {
        display: flex;
        align-items: center;
    }
`;

const PreviousSchoolRemoveButton = styled(Button)`
    && {
        width: 40px;
        height: 40px;
        padding: 0 !important;
        border-radius: 12px;
        border: 1px solid rgba(239, 68, 68, 0.35) !important;
        background: rgba(239, 68, 68, 0.06) !important;
        color: #ef4444 !important;
    }
`;

const ProfileImageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const ProfileImagePreview = styled.div`
    width: 84px;
    height: 84px;
    border-radius: 14px;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#fafafa')};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const UploadHelp = styled.div`
    margin-top: 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

function toDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

function toDayjs(value) {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (typeof value?.toDate === 'function') {
        const out = dayjs(value.toDate());
        return out.isValid() ? out : null;
    }
    const out = dayjs(value instanceof Date ? value : value);
    return out.isValid() ? out : null;
}

const FormSectionCard = styled.div`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 16px;
    padding: 14px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
`;

const IconWrap = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(240, 74, 74, 0.12);
    color: ${({ theme }) => theme.colors.primary};
`;

const SectionTitle = styled.div`
    font-family: var(--font-family-lufga);
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionDivider = styled.div`
    height: 1px;
    background: ${({ theme }) => theme.colors.border.default};
    margin: 12px 0;
`;

export default function Teachers() {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const { dateFormat, dateInputFormats } = useTenantDateFormat();
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const { rows, total, loading, kpis } = useTeachers({ tenantId, page: currentPage, pageSize: currentPageSize });
    const user = useAuthStore((s) => s.user);
    const showDownload = useMemo(() => {
        if (!user) return false;
        if (
            user.role === 'tenant' ||
            user.role === 'owner' ||
            user.role === 'admin' ||
            user.staffRole === 'admin' ||
            user.permissions?.includes('*')
        ) return true;
        return user.permissions?.includes('teacherReport.view') || user.permissions?.includes('teacherReport.full');
    }, [user]);

    const handleFetchAllTeachers = async (onProgress) => {
        let allRows = [];
        let cursor = null;
        let hasMore = true;
        const pageSize = 50;
        let currentLoaded = 0;

        while (hasMore) {
            const res = await fetchTeachersPage({
                tenantId,
                pageSize,
                cursor,
            });
            allRows = [...allRows, ...res.rows];
            currentLoaded += res.rows.length;

            const totalEst = search ? filteredRows.length : (res.total || kpis.totalTeachers || 100);
            onProgress(Math.min(currentLoaded, totalEst), totalEst);

            if (res.rows.length < pageSize || !res.nextCursor) {
                hasMore = false;
            } else {
                cursor = res.nextCursor;
            }
        }

        if (search) {
            const queryClean = search.toLowerCase().trim();
            allRows = allRows.filter((row) => {
                const displayName = (row.displayName || `${row.firstName || ''} ${row.lastName || ''}`).toLowerCase();
                const email = (row.email || '').toLowerCase();
                const teacherId = (row.teacherId || '').toLowerCase();
                const dept = (row.currentPosition?.department || '').toLowerCase();
                const desig = (row.currentPosition?.designation || '').toLowerCase();
                return (
                    displayName.includes(queryClean) ||
                    email.includes(queryClean) ||
                    teacherId.includes(queryClean) ||
                    dept.includes(queryClean) ||
                    desig.includes(queryClean)
                );
            });
        }

        return allRows;
    };

    const onExportExcel = async (onProgress) => {
        const data = await handleFetchAllTeachers(onProgress);
        exportToExcel(
            data,
            [
                { title: 'Teacher Name', dataIndex: 'displayName' },
                { title: 'Teacher ID', dataIndex: 'teacherId' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Phone', dataIndex: 'phone' },
                { title: 'Department', dataIndex: 'currentPosition.department' },
                { title: 'Designation', dataIndex: 'currentPosition.designation' },
                { title: 'Status', dataIndex: 'currentPosition.status' },
            ],
            `teachers_${tenantId}.xlsx`
        );
    };

    const onExportPdf = async (onProgress) => {
        const data = await handleFetchAllTeachers(onProgress);
        exportToPdf(
            data,
            [
                { title: 'Teacher Name', dataIndex: 'displayName' },
                { title: 'Teacher ID', dataIndex: 'teacherId' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Phone', dataIndex: 'phone' },
                { title: 'Department', dataIndex: 'currentPosition.department' },
                { title: 'Designation', dataIndex: 'currentPosition.designation' },
                { title: 'Status', dataIndex: 'currentPosition.status' },
            ],
            'Teachers Report',
            `teachers_${tenantId}.pdf`
        );
    };

    const { exceeded: limitExceeded } = useModuleLimit({ moduleKey: 'teachers', currentCount: total });
    const { rows: tenantSubjects, loading: tenantSubjectsLoading } = useTenantSubjects({ tenantId });
    const { rows: tenantClasses, loading: tenantClassesLoading } = useTenantClasses({ tenantId });
    const createTeacherMutation = useCreateTeacher({ tenantId });
    const updateTeacherMutation = useUpdateTeacher({ tenantId });
    const softDeleteTeacherMutation = useSoftDeleteTeacher({ tenantId });
    const { requestStatusChange, StatusChangeModal } = useDirectoryStatusChangeFlow();
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create'); // create | edit | view
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [form] = Form.useForm();
    const [rowMenu, setRowMenu] = useState(null);

    const filteredRows = useMemo(() => {
        const q = (search || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const hay = [
                r.teacherId,
                r.firstName,
                r.lastName,
                r.email,
                r.phone,
                r?.currentPosition?.department,
                r?.currentPosition?.designation,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [rows, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const teacherDetailId = useMemo(() => {
        if ((mode !== 'edit' && mode !== 'view') || !editingTeacher) return null;
        return editingTeacher.id || editingTeacher.authUid || null;
    }, [mode, editingTeacher]);

    const teacherSubjectAssignmentRows = useMemo(() => {
        if (!teacherDetailId) return [];
        const tid = String(teacherDetailId);
        const classMap = new Map((tenantClasses || []).map((c) => [c.id, c]));
        const out = [];
        for (const sub of tenantSubjects || []) {
            if (String(sub?.assignedTeacherId || '') !== tid) continue;
            const classIds = Array.isArray(sub.classIds) ? sub.classIds : [];
            for (const cid of classIds) {
                const c = classMap.get(cid);
                const classLabel = (c?.displayName || c?.name || '').trim() || cid;
                out.push({
                    key: `${sub.id}:${cid}`,
                    classLabel,
                    subjectName: sub.name || '—',
                    code: sub.code || '—',
                });
            }
        }
        return out;
    }, [teacherDetailId, tenantSubjects, tenantClasses]);

    const subjectAssignmentColumns = useMemo(
        () => [
            { title: 'Class', dataIndex: 'classLabel', key: 'classLabel', render: (v) => v || '—' },
            { title: 'Subject', dataIndex: 'subjectName', key: 'subjectName', render: (v) => v || '—' },
            { title: 'Code', dataIndex: 'code', key: 'code', render: (v) => v || '—' },
        ],
        []
    );

    /** Count of subject documents (directory) where `assignedTeacherId` matches the teacher doc id. */
    const assignedSubjectCountByTeacherId = useMemo(() => {
        const m = new Map();
        for (const sub of tenantSubjects || []) {
            const aid = sub?.assignedTeacherId;
            if (aid == null || String(aid).trim() === '') continue;
            const key = String(aid);
            m.set(key, (m.get(key) || 0) + 1);
        }
        return m;
    }, [tenantSubjects]);

    const teacherDisplayName = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Teacher';
    const isTeacherActive = (r) => (r?.currentPosition?.status || 'active') !== 'inactive';

    const handleTeacherStatusSwitch = useCallback(
        async (row, nextActive) => {
            const cur = isTeacherActive(row);
            if (nextActive === cur) return;

            const teacherId = row.id || row.authUid;
            if (!teacherId) {
                message.error('Missing teacher id');
                return;
            }
            if (!nextActive) {
                if (tenantSubjectsLoading) {
                    message.warning('Subject assignments are still loading. Try again in a moment.');
                    return;
                }
                const assigned = getSubjectsAssignedToTeacher(teacherId, tenantSubjects);
                if (assigned.length) {
                    Modal.warning({
                        title: 'Unassign subjects first',
                        content: (
                            <div>
                                <p>This teacher is still assigned to:</p>
                                <p style={{ marginTop: 8 }}>
                                    <strong>{formatSubjectAssignmentList(assigned)}</strong>
                                </p>
                                <p style={{ marginTop: 12 }}>
                                    Open <strong>Classes & Badge</strong>, tab <strong>Classes & subjects</strong>, open the class,
                                    then use <strong>Unassign</strong> on each subject (or clear the teacher in Edit subject) before setting
                                    inactive.
                                </p>
                            </div>
                        ),
                        className: 'app-modal-confirm--themed',
                    });
                    return;
                }
            }

            const res = await requestStatusChange({
                displayName: teacherDisplayName(row),
                nextActive,
            });
            if (!res.ok) return;
            setUpdatingStatusId(teacherId);
            try {
                await updateTeacherMutation.mutateAsync({
                    teacherId,
                    patch: {
                        currentPosition: {
                            ...(row.currentPosition || {}),
                            status: nextActive ? 'active' : 'inactive',
                            inactiveReason: nextActive ? null : res.reason,
                        },
                    },
                });
                message.success(nextActive ? 'Teacher activated' : 'Teacher deactivated');
            } catch (e) {
                message.error(e?.message || 'Failed to update status');
            } finally {
                setUpdatingStatusId(null);
            }
        },
        [requestStatusChange, updateTeacherMutation, tenantSubjects, tenantSubjectsLoading]
    );

    const columns = useMemo(
        () => [
            {
                title: 'Teacher',
                key: 'teacher',
                render: (_, row) => {
                    const displayName =
                        row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';
                    return (
                        <Space>
                            <AppAvatar
                                src={row.profileImage || row.avatarUrl || row.avatarDataUrl}
                                name={displayName}
                                seed={row?.id || row?.authUid || row?.email || displayName}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <Text strong style={{ lineHeight: 1.2 }}>{displayName}</Text>
                                <Text
                                    type="secondary"
                                    style={{ lineHeight: 1.2, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {row.email || '—'}
                                </Text>
                            </div>
                        </Space>
                    );
                },
            },
            { title: 'Teacher ID', dataIndex: 'teacherId', key: 'teacherId', render: (v) => v || '—' },
            {
                title: 'Department',
                key: 'department',
                render: (_, r) => {
                    const dept = r?.currentPosition?.department;
                    const designation = r?.currentPosition?.designation;
                    if (dept && designation) return `${dept} · ${designation}`;
                    return dept || designation || '—';
                },
            },
            { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v) => v || '—' },
            {
                title: 'Subjects',
                key: 'assignedSubjects',
                render: (_, row) => {
                    if (tenantSubjectsLoading) return '—';
                    const tid = row.id || row.authUid;
                    if (!tid) return 0;
                    return assignedSubjectCountByTeacherId.get(String(tid)) || 0;
                },
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, row) => {
                    const active = isTeacherActive(row);
                    const tip = !active && row.currentPosition?.inactiveReason
                        ? row.currentPosition.inactiveReason
                        : active ? 'Active' : 'Inactive';
                    const tid = row.id || row.authUid;
                    return (
                        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                            <Tooltip title={tip}>
                                <Switch
                                    checked={active}
                                    loading={updatingStatusId === tid}
                                    onChange={(checked) => void handleTeacherStatusSwitch(row, checked)}
                                />
                            </Tooltip>
                        </span>
                    );
                },
            },
        ],
        [
            handleTeacherStatusSwitch,
            updatingStatusId,
            assignedSubjectCountByTeacherId,
            tenantSubjectsLoading,
        ]
    );

    const closeAndReset = useCallback(() => {
        setOpen(false);
        setMode('create');
        setEditingTeacher(null);
        form.resetFields();
        setProfileImagePreview(null);
    }, [form]);

    const populateFormFromRow = useCallback(
        (row) => {
            setEditingTeacher(row);
            setProfileImagePreview(row?.profileImage || null);
            form.setFieldsValue({
                ...row,
                dateOfBirth: toDayjs(row?.dateOfBirth),
                address: row?.address || {},
                qualification: row?.qualification || {},
                experience: row?.experience || {},
                currentPosition: {
                    ...(row?.currentPosition || {}),
                    joiningDate: toDayjs(row?.currentPosition?.joiningDate),
                },
                emergencyContact: row?.emergencyContact || {},
                bankDetails: row?.bankDetails || {},
            });
        },
        [form]
    );

    const openView = useCallback(
        (row) => {
            setMode('view');
            populateFormFromRow(row);
            setOpen(true);
        },
        [populateFormFromRow]
    );

    const openEdit = useCallback(
        (row) => {
            setMode('edit');
            populateFormFromRow(row);
            setOpen(true);
        },
        [populateFormFromRow]
    );

    const confirmSoftDelete = useCallback(
        (row) => {
            const name = row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'This teacher';
            const teacherId = row.id || row.authUid;
            if (!teacherId) {
                message.error('Missing teacher id');
                return;
            }
            if (tenantSubjectsLoading) {
                message.warning('Subject assignments are still loading. Try again in a moment.');
                return;
            }
            const assigned = getSubjectsAssignedToTeacher(teacherId, tenantSubjects);
            if (assigned.length) {
                Modal.warning({
                    title: 'Unassign subjects first',
                    content: (
                        <div>
                            <p>
                                <strong>{name}</strong> is still assigned to:
                            </p>
                            <p style={{ marginTop: 8 }}>
                                <strong>{formatSubjectAssignmentList(assigned)}</strong>
                            </p>
                            <p style={{ marginTop: 12 }}>
                                Open <strong>Classes & Badge</strong> → <strong>Classes & subjects</strong>, open each class, and use{' '}
                                <strong>Unassign</strong> on the subject row (or clear the teacher when editing) before removing this teacher
                                from the directory.
                            </p>
                        </div>
                    ),
                    className: 'app-modal-confirm--themed',
                });
                return;
            }
            Modal.confirm({
                title: 'Remove from directory?',
                content: `${name} will be hidden from this list. The database record is kept with delete flagged; summary counts will update.`,
                okText: 'Remove',
                okType: 'danger',
                cancelText: 'Cancel',
                className: 'app-modal-confirm--themed',
                onOk: async () => {
                    await softDeleteTeacherMutation.mutateAsync({ teacherId });
                    message.success('Teacher removed from directory');
                    const eid = editingTeacher?.id || editingTeacher?.authUid;
                    if (open && eid && String(eid) === String(teacherId)) closeAndReset();
                },
            });
        },
        [softDeleteTeacherMutation, closeAndReset, editingTeacher, open, tenantSubjects, tenantSubjectsLoading]
    );

    useEffect(() => {
        if (!rowMenu) return undefined;
        const closeMenu = () => setRowMenu(null);
        window.addEventListener('click', closeMenu);
        window.addEventListener('scroll', closeMenu, true);
        window.addEventListener('resize', closeMenu);
        return () => {
            window.removeEventListener('click', closeMenu);
            window.removeEventListener('scroll', closeMenu, true);
            window.removeEventListener('resize', closeMenu);
        };
    }, [rowMenu]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const { email, password, confirmPassword, isDelete: _omitIsDelete, ...profile } = values;

            if (mode === 'create') {
                if (password !== confirmPassword) {
                    message.error('Passwords do not match');
                    return;
                }

                await createTeacherMutation.mutateAsync({
                    email,
                    password,
                    profile: {
                        ...profile,
                        profileImage: profileImagePreview || null,
                        periods: [],
                    },
                });

                message.success('Teacher created');
            } else if (mode === 'edit') {
                const teacherId = editingTeacher?.id || editingTeacher?.authUid;
                if (!teacherId) throw new Error('Missing teacher id');

                await updateTeacherMutation.mutateAsync({
                    teacherId,
                    patch: {
                        ...profile,
                        profileImage: profileImagePreview ?? editingTeacher?.profileImage ?? null,
                    },
                });

                message.success('Teacher updated');
            }
            if (mode === 'create' || mode === 'edit') closeAndReset();
        } catch (e) {
            const msg =
                e?.message ||
                (mode === 'create' ? 'Failed to create teacher' : mode === 'edit' ? 'Failed to update teacher' : '');
            if (msg !== 'Validation failed') message.error(msg);
        }
    };

    return (
        <DashboardContainer>
            {StatusChangeModal}
            <TeacherRegisterModalStyles />

            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Teachers</StudentsHeaderTitle>
                    <StudentsHeaderSub>Manage teacher profiles and portal accounts</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <StudentsHeaderActions>
                    <Tooltip title={limitExceeded ? "Limit is exceeded. Please contact EDULINK to change your Subscription Plan." : ""}>
                        <Button
                            type="primary"
                            disabled={limitExceeded}
                            onClick={() => {
                                setMode('create');
                                setEditingTeacher(null);
                                setProfileImagePreview(null);
                                form.resetFields();
                                setOpen(true);
                            }}
                        >
                            Create Teacher
                        </Button>
                    </Tooltip>
                </StudentsHeaderActions>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={4} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Total Teachers" value={kpis.totalTeachers} />
                <KPICard title="Active" value={kpis.activeTeachers} />
                <KPICard title="Inactive" value={kpis.inactiveTeachers} />
                <KPICard title="Deleted (directory)" value={kpis.deletedTeachers ?? 0} />
            </KPICardsGrid>

            <Table
                title="Teacher Information"
                columns={columns}
                dataSource={filteredRows}
                searchValue={search}
                onSearchChange={(v) => setSearch(v)}
                showFilter={false}
                showMore={false}
                pagination
                pageSize={currentPageSize}
                serverPagination
                currentPage={currentPage}
                totalItems={search ? filteredRows.length : total}
                onPageChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== currentPageSize) {
                        setCurrentPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                $topMargin
                loading={loading}
                showDownload={showDownload}
                onExportExcel={onExportExcel}
                onExportPdf={onExportPdf}
                onRowClick={openView}
                getRowProps={(record) => ({
                    onContextMenu: (event) => {
                        event.preventDefault();
                        setRowMenu({
                            record,
                            x: event.clientX,
                            y: event.clientY,
                        });
                    },
                })}
            />

            <AppContextMenu
                open={!!rowMenu}
                position={rowMenu ? { x: rowMenu.x, y: rowMenu.y } : null}
                onClose={() => setRowMenu(null)}
                items={!rowMenu ? [] : [
                    {
                        key: 'view',
                        icon: <EyeOutlined />,
                        label: 'View',
                        onClick: async () => {
                            openView(rowMenu.record);
                        },
                    },
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: 'Edit',
                        onClick: async () => {
                            openEdit(rowMenu.record);
                        },
                    },
                    {
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: 'Delete from directory',
                        danger: true,
                        onClick: async () => {
                            confirmSoftDelete(rowMenu.record);
                        },
                    },
                ]}
            />

            <AppModal
                open={open}
                onCancel={closeAndReset}
                title={
                    mode === 'create' ? 'Register Teacher' : mode === 'view' ? 'View Teacher' : 'Edit Teacher'
                }
                className="teacher-register-modal"
                width="86vw"
                style={{ maxWidth: 960 }}
                footer={
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
                        <Space>
                            <Button $primary={false} onClick={closeAndReset}>
                                {mode === 'view' ? 'Close' : 'Cancel'}
                            </Button>
                            {mode === 'view' && editingTeacher ? (
                                <Button type="primary" onClick={() => setMode('edit')}>
                                    Edit
                                </Button>
                            ) : null}
                            {mode === 'create' || mode === 'edit' ? (
                                <Button
                                    type="primary"
                                    loading={
                                        mode === 'create'
                                            ? createTeacherMutation.isPending
                                            : updateTeacherMutation.isPending
                                    }
                                    onClick={handleSubmit}
                                >
                                    {mode === 'create' ? 'Save Teacher' : 'Save Changes'}
                                </Button>
                            ) : null}
                        </Space>
                    </div>
                }
            >
                <Form
                    disabled={mode === 'view'}
                    form={form}
                    layout="vertical"
                    initialValues={{
                        gender: 'male',
                        currentPosition: { status: 'active' },
                        experience: { totalYears: 0, previousSchools: [] },
                        qualification: { yearOfPassing: new Date().getFullYear(), subjects: [] },
                    }}
                >
                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Users size={18} />
                            </IconWrap>
                            <SectionTitle>Basic Information</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="teacherId" label="Teacher ID">
                                    <AppInput placeholder="Enter unique teacher ID" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="firstName" label="First Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="lastName" label="Last Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={mode === 'create' ? [{ required: true, type: 'email' }] : []}
                                >
                                    <AppInput disabled={mode !== 'create'} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="phone" label="Phone">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="dateOfBirth" label="Date of Birth">
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="gender" label="Gender">
                                    <AppSelect
                                        options={[
                                            { value: 'male', label: 'Male' },
                                            { value: 'female', label: 'Female' },
                                            { value: 'other', label: 'Other' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item label="Profile Image">
                                    <ProfileImageRow>
                                        <ProfileImagePreview>
                                            {profileImagePreview ? (
                                                <img src={profileImagePreview} alt="Profile preview" />
                                            ) : (
                                                <UserOutlined style={{ fontSize: 26, opacity: 0.45 }} />
                                            )}
                                        </ProfileImagePreview>
                                        <div>
                                            <Upload
                                                accept="image/*"
                                                maxCount={1}
                                                showUploadList={false}
                                                beforeUpload={async (file) => {
                                                    if (!file.type?.startsWith?.('image/')) {
                                                        message.error('Please select an image file');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        message.error('Please select an image smaller than 5MB');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    const dataUrl = await toDataUrl(file);
                                                    setProfileImagePreview(dataUrl);
                                                    return false;
                                                }}
                                            >
                                                <Button $primary={false} icon={<UploadOutlined />}>
                                                    Upload Image
                                                </Button>
                                            </Upload>
                                            <UploadHelp>JPG/PNG (Max 5MB)</UploadHelp>
                                        </div>
                                    </ProfileImageRow>
                                </Form.Item>
                            </Col>
                            {mode === 'create' ? (
                                <>
                                    <Col xs={24} md={12} lg={8}>
                                        <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                                            <AppPasswordInput />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12} lg={8}>
                                        <Form.Item name="confirmPassword" label="Confirm Password" rules={[{ required: true, min: 6 }]}>
                                            <AppPasswordInput />
                                        </Form.Item>
                                    </Col>
                                </>
                            ) : null}
                        </Row>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Home size={18} />
                            </IconWrap>
                            <SectionTitle>Address Information</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={24} lg={12}>
                                <Form.Item name={['address', 'street']} label="Street">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['address', 'city']} label="City">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['address', 'state']} label="State">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['address', 'zipCode']} label="Zip Code">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['address', 'country']} label="Country">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <GraduationCap size={18} />
                            </IconWrap>
                            <SectionTitle>Qualification</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['qualification', 'degree']} label="Degree">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['qualification', 'institution']} label="Institution">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['qualification', 'yearOfPassing']} label="Year of Passing">
                                    <AppInput type="number" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.List name={['qualification', 'subjects']}>
                            {(fields, { add, remove }) => (
                                <>
                                    <Text strong>Subjects</Text>
                                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                                        {fields.map((field) => (
                                            <Col key={field.key} xs={24} md={12} lg={8}>
                                                <Form.Item {...field} label="Subject">
                                                    <AppInput
                                                        addonAfter={
                                                            <Button type="text" onClick={() => remove(field.name)}>
                                                                Remove
                                                            </Button>
                                                        }
                                                    />
                                                </Form.Item>
                                            </Col>
                                        ))}
                                        <Col xs={24}>
                                            <Button onClick={() => add()}>Add Subject</Button>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Form.List>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Briefcase size={18} />
                            </IconWrap>
                            <SectionTitle>Experience & Current Position</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['experience', 'totalYears']} label="Total Years">
                                    <AppInput type="number" min={0} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'department']} label="Department">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'designation']} label="Designation">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'joiningDate']} label="Joining Date">
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'salary']} label="Salary" rules={[{ required: true }]}>
                                    <AppInput type="number" min={0} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.List name={['experience', 'previousSchools']}>
                            {(fields, { add, remove }) => (
                                <>
                                    <Text strong>Previous Schools</Text>
                                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                                        {fields.map((field) => (
                                            <Col key={field.key} xs={24}>
                                                <Row gutter={[12, 12]}>
                                                    <Col xs={24} md={12} lg={8}>
                                                        <Form.Item name={[field.name, 'schoolName']} label="School Name">
                                                            <AppInput />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} md={12} lg={8}>
                                                        <Form.Item name={[field.name, 'position']} label="Position">
                                                            <AppInput />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} md={12} lg={6}>
                                                        <Form.Item name={[field.name, 'duration']} label="Duration">
                                                            <AppInput />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col
                                                        xs={24}
                                                        md={12}
                                                        lg={2}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            paddingTop: 10,
                                                        }}
                                                    >
                                                        <PreviousSchoolRemoveButton
                                                            $primary={false}
                                                            $hoverShadow={false}
                                                            aria-label="Remove previous school"
                                                            onClick={() => remove(field.name)}
                                                        >
                                                            <Trash2 size={18} />
                                                        </PreviousSchoolRemoveButton>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        ))}
                                        <Col xs={24}>
                                            <Button onClick={() => add({})}>Add Previous School</Button>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Form.List>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <PhoneCall size={18} />
                            </IconWrap>
                            <SectionTitle>Emergency Contact</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['emergencyContact', 'name']} label="Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['emergencyContact', 'relationship']} label="Relationship">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['emergencyContact', 'phone']} label="Phone">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['emergencyContact', 'email']} label="Email">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Landmark size={18} />
                            </IconWrap>
                            <SectionTitle>Bank Details</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['bankDetails', 'accountNumber']} label="Account Number">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['bankDetails', 'bankName']} label="Bank Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['bankDetails', 'ifscCode']} label="IFSC Code">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name={['bankDetails', 'accountHolderName']} label="Account Holder Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>
                </Form>

                {(mode === 'edit' || mode === 'view') && teacherDetailId ? (
                    <>
                        <SectionDivider />
                        <FormSectionCard>
                            <CardHeader>
                                <IconWrap>
                                    <GraduationCap size={18} />
                                </IconWrap>
                                <SectionTitle>Subject assignments</SectionTitle>
                            </CardHeader>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                                Classes and subjects where this teacher is assigned (from the subject directory).
                            </Text>
                            <Spin spinning={tenantSubjectsLoading || tenantClassesLoading}>
                                <Table
                                    title=""
                                    columns={subjectAssignmentColumns}
                                    dataSource={teacherSubjectAssignmentRows}
                                    emptyText="No subject assignments for this teacher"
                                    showSearch={false}
                                    showMore={false}
                                    showFilter={false}
                                    pagination={teacherSubjectAssignmentRows.length > 10}
                                    pageSize={10}
                                    $topMargin={false}
                                />
                            </Spin>
                        </FormSectionCard>
                    </>
                ) : null}
            </AppModal>

        </DashboardContainer>
    );
}

